import { BackLink, FaqComponent, Icon } from '@arcadiapower/shrike';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '~/components/button';
import { FaqButton } from '~/components/button/button.style';
import { Wrapper } from '~/components/customer-details/customer-details.style';
import { COMMUNITY_SOLAR, DialogSelector } from '~/components/dialogs';
import { Disclaimer } from '~/components/disclaimer';
import { OregonDisclaimer, OregonEmailLink } from '~/components/disclaimer/disclaimer.style';
import { ErrorMessage } from '~/components/error-message';
import { Form } from '~/components/form';
import { GoogleSignup } from '~/components/google-signup';
import {
  type ImageType,
  ImageSelector,
  CREATE_ACCOUNT_IMAGE,
  GLOBE_HOME,
  OREGON_DISCLAIMER,
} from '~/components/image';
import { Inputs } from '~/components/inputs';
import { DisabledInput } from '~/components/inputs/disabled-input';
import { Container, TwoColumnGrid } from '~/components/layout';
import { Link } from '~/components/link';
import { Separator } from '~/components/separator';
import { GeneralTerms } from '~/components/terms/general-terms';
import { WaitlistTerms } from '~/components/terms/waitlist-terms';
import { Description, Title, Text } from '~/components/typography';
import { COMMUNITY_SOLAR_FUNNEL_ID } from '~/funnels/community-solar/config';
import { GO_SOLAR_FUNNEL_ID } from '~/funnels/go-solar/config';
import { MINNESOTA_LMI_FUNNEL_ID } from '~/funnels/minnesota-lmi/config';
import { SOLAR_LANDSCAPE_FUNNEL_ID } from '~/funnels/solar-landscape/config';
import { useCopy, useForm } from '~/hooks';
import { LDClient, LDFlags } from '~/providers';
import { faqData } from '~/screens/create-account/faq.data';
import { Utility } from '~/typings/context';
import { FunnelTrack } from '~/typings/graphql';
import { LOGIN_URL } from '~/utils/constants';
import { getSavingsRateDescription } from '~/utils/funnels';
import { TRACK } from '~/utils/tracking';
import { MessageWrapper, VirginiaText } from './create-account.styles';

export const CreateAccount = ({
  context,
  error,
  flags,
  goTo,
  next,
  overrideCopy,
  pending,
}: CreateAccountProps) => {
  const disableEmail = !!flags?.disableEmail;
  const hidePassword = !!flags?.hidePassword;
  const showStripeIdInputs = !!flags?.showStripeIdInputs;
  const isInviteCodeRequired = !!flags?.isInviteCodeRequired;
  const hideGoogleButton = !!flags?.hideGoogleButton;
  const hideTerms = !!flags?.hideTerms;
  const showPhone = !!(flags?.showPhone || flags?.requirePhone);
  const showBusinessName = !!flags?.requireBusinessName;
  const showInviteCode = !!flags?.showInviteCode || !!context.inviteCode;
  const showAgentName = useMemo(() => {
    return (
      context.funnelId === GO_SOLAR_FUNNEL_ID &&
      context.utility.state.abbreviation === 'NM' &&
      !context.forceWaitlist &&
      !context.agentName
    );
  }, []);
  const isOregonTiers =
    context.funnelId === 'oregonTier1' ||
    context.funnelId === 'oregonTier2' ||
    context.funnelId === 'oregonTier1Lmi' ||
    context.funnelId === 'oregonTier2Lmi';
  const [shouldShowOptInSMSOption, setShouldShowOptInSMSOption] = useState(false);
  const [showBillingCheckbox, setShowBillingCheckbox] = useState(false);
  const copy = useCopy(context, checkCopy, overrideCopy);
  const faqRef = useRef<HTMLDivElement>(null);
  const backToTopRef = useRef<HTMLDivElement>(null);
  const [form, updateForm, isValid] = useForm({
    email: context.email,
    firstName: context.firstName,
    lastName: context.lastName,
    ...(!hideTerms && { generalContractIds: [] }),
    ...(!hidePassword && { password: context.password }),
    ...(showPhone && { phone: context.phone }),
    ...(showPhone &&
      LDClient.variation(LDFlags.showSmsOptInCheckbox) && { optInToSMS: context.optInToSMS }),
    ...(showBusinessName && { businessName: context.businessName }),
    ...(showInviteCode && { inviteCode: context.inviteCode }),
    ...(showAgentName && { agentName: context.agentName }),
    ...(showBillingCheckbox && { budgetBilling: context.budgetBilling }),
  });
  const showVirginiaMessage = () => {
    const isVirginia = context.utility.state.abbreviation === 'VA';

    return (
      isVirginia &&
      context.utility.funnelTrack !== FunnelTrack.Waitlist &&
      context.funnelId === COMMUNITY_SOLAR_FUNNEL_ID
    );
  };

  const isMD = context.utility.state.abbreviation === 'MD';
  const isSolarLandscape = context.funnelId === SOLAR_LANDSCAPE_FUNNEL_ID;
  const isGoSolar = context.funnelId === GO_SOLAR_FUNNEL_ID;
  const isCommunitySolar = context.funnelId === COMMUNITY_SOLAR_FUNNEL_ID;
  const showLMISavingsMessage = () => {
    return isSolarLandscape || (isMD && (isGoSolar || isCommunitySolar));
  };

  const getTerms = context => {
    return context.utility.funnelTrack === FunnelTrack.Waitlist ? (
      <WaitlistTerms onChange={updateForm} />
    ) : (
      <GeneralTerms onChange={updateForm} />
    );
  };

  useEffect(() => {
    if (LDClient.variation(LDFlags.showSmsOptInCheckbox)) {
      setShouldShowOptInSMSOption(true);
    }
    LDClient.on('change', () => {
      setShouldShowOptInSMSOption(LDClient.variation(LDFlags.showSmsOptInCheckbox));
    });
  }, []);

  useEffect(() => {
    if (LDClient.variation(LDFlags.showBudgetBillingCheckBox) && !!flags?.showBillingCheckbox) {
      setShowBillingCheckbox(true);
    }
  }, []);

  return (
    <Wrapper ref={backToTopRef} style={{ border: 'none' }}>
      <TwoColumnGrid testId="screen-CreateAccount">
        {!flags?.hideBackLink ? (
          <TwoColumnGrid.Navigation>
            <BackLink openInNewTab={false} onClick={goTo?.utility} data-testid="utility-backlink">
              Back
            </BackLink>
          </TwoColumnGrid.Navigation>
        ) : null}
        <TwoColumnGrid.Primary>
          {!!flags?.showStep && (
            <Text color="secondary" fontWeight="700" fontSize="16px">
              {copy.step}
            </Text>
          )}
          <Title fontWeight={700} fontSize="28px">
            {copy.title}
          </Title>
          <Description mb={5}>{copy.description}</Description>
          {!!copy.descriptionTwo && <Description mb={5}>{copy.descriptionTwo}</Description>}
          {!flags?.hideLearnMoreDialogSelector ? (
            <Description mb={6}>
              {showLMISavingsMessage() ? (
                <Text fontSize="14px" paddingBottom="8px">
                  {copy.lmiSavingsPercentageDescription}
                </Text>
              ) : null}
              <DialogSelector
                type={copy.learnMoreModal}
                // @ts-expect-error copies are complicatedly typed fixing this error
                // means significant refactoring. TODO.
                copy={copy.learnMoreCopy}
                target={
                  <Link color="primary" track={TRACK.learnMoreCsClicked}>
                    {copy.learnMoreText}
                  </Link>
                }
              />
            </Description>
          ) : null}
          {flags?.showFaqButton ? (
            <FaqButton
              fullWidth={false}
              mode="default"
              onClick={() => {
                faqRef?.current?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {' '}
              <Icon
                color="primary"
                icon="InfoCircleInverted"
                margin="5px"
                opacity="high"
                path="M 1,3 A 2,2 0,0,1 5,3 A 2,2 0,0,1 9,3 Q 9,6 5,9 Q 1,6 1,3 z"
                scale={2}
                title="InfoCircleInverted"
              />
              View FAQs
            </FaqButton>
          ) : null}
          {showVirginiaMessage() && (
            <MessageWrapper>
              <Icon icon="InfoCircleInverted" margin={{ top: '2px' }} />
              <VirginiaText>{copy.virginiaInformation}</VirginiaText>
            </MessageWrapper>
          )}
          {!hideGoogleButton && (
            <>
              <GoogleSignup error={context.googleUserError} />
              <Separator innerText="or" />
            </>
          )}
          <Form isValid={isValid} onSubmit={() => next(form as CreateAccountForm)}>
            <Container addInputMargin>
              {!flags?.showZipCode ? (
                <DisabledInput testId="utility-name" label="Electric utility">
                  {context.utility.displayName}
                </DisabledInput>
              ) : null}
              {!!showBusinessName && (
                <Inputs.BusinessName onChange={updateForm} value={form.businessName} />
              )}
              <Inputs.FirstName onChange={updateForm} value={form.firstName} />
              <Inputs.LastName onChange={updateForm} value={form.lastName} />
              {!!showPhone && (
                <Inputs.Phone
                  onChange={updateForm}
                  value={form.phone}
                  optional={!flags.requirePhone}
                />
              )}
              <Inputs.Email readOnly={disableEmail} onChange={updateForm} value={form.email} />
              {flags?.showZipCode ? (
                <>
                  <DisabledInput testId="utility-name" label="Zip Code">
                    {context.zipCode}
                  </DisabledInput>
                  <DisabledInput testId="utility-name" label="Electric utility">
                    {context.utility.displayName}
                  </DisabledInput>
                </>
              ) : null}
              {!hidePassword && <Inputs.Password onChange={updateForm} value={form.password} />}
              {showInviteCode && (
                <Inputs.InviteCode
                  disabled={!!context.inviteCode}
                  onChange={updateForm}
                  optional={!isInviteCodeRequired}
                  value={form.inviteCode}
                />
              )}
              {showAgentName && <Inputs.AgentName onChange={updateForm} value={form.agentName} />}
              {showStripeIdInputs ? (
                <>
                  <Separator innerText="Payment" />
                  <Inputs.StripeCustomerId onChange={updateForm} value={form.stripeCustomerId} />
                  <Inputs.StripePaymentId onChange={updateForm} value={form.stripePaymentId} />
                  <Separator />
                </>
              ) : null}
            </Container>
            {!!showBillingCheckbox && (
              <Inputs.BudgetBillingCheckbox
                checked={form.budgetBilling}
                value={showStripeIdInputs}
                onChange={updateForm}
              />
            )}
            {shouldShowOptInSMSOption && (
              <Inputs.OptInToSmsCheckbox
                checked={form.optInToSMS || showStripeIdInputs}
                initialValue={showStripeIdInputs || form.optInToSMS}
                onChange={updateForm}
              />
            )}
            {!hideTerms ? getTerms(context) : null}
            <Container mt={6}>
              <ErrorMessage mb={2} error={error} />
              <Button disabled={!isValid} loading={pending} type="submit" mode="primary">
                {copy.next}
              </Button>
            </Container>
          </Form>
          <Text textAlign="center" mt={7}>
            Have an Arcadia account?{' '}
            <Link color="primary" openInNewTab={false} href={LOGIN_URL}>
              Sign in
            </Link>
            .
          </Text>
          {flags?.hideDisclaimer ? <Disclaimer mt={7}>{copy.disclaimer}</Disclaimer> : null}
          {!!isOregonTiers && (
            <>
              <Separator />
              <ImageSelector type={copy.oregonDisclaimerImageType} />
              <OregonDisclaimer mt={7}>
                {copy.oregonDisclaimerText}
                <OregonEmailLink href="mailto: info@oregoncsp.org">
                  info@oregoncsp.org
                </OregonEmailLink>
                .
              </OregonDisclaimer>
            </>
          )}
        </TwoColumnGrid.Primary>
        <TwoColumnGrid.Secondary style={{ marginLeft: '160px' }}>
          {flags?.showFaqButton ? (
            <Wrapper
              gridColumn="auto / span 2"
              display="flex"
              flexGrow={1}
              style={{ borderRadius: '0px' }}
            >
              <FaqComponent data={faqData} header="Frequently Asked Questions" />
            </Wrapper>
          ) : (
            <ImageSelector type={copy.imageType} />
          )}
        </TwoColumnGrid.Secondary>
        {flags?.showFaqButton ? (
          <TwoColumnGrid.MobileOnlySecondary>
            <Wrapper
              ref={faqRef}
              gridColumn="auto / span 2"
              display="flex"
              flexGrow={1}
              style={{ borderRadius: '0px' }}
            >
              <FaqComponent data={faqData} header="Frequently Asked Questions" />
            </Wrapper>
            <Wrapper
              onClick={() => {
                backToTopRef?.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                textDecoration: 'underline',
                textUnderlineOffset: ' 2px',
              }}
            >
              <Text textAlign="center" mt={5} paddingBottom={5} textStyle="heading600">
                Back to top
                <Icon
                  color="primary"
                  icon="ArrowUp"
                  margin="5px"
                  opacity="high"
                  path="M 1,3 A 2,2 0,0,1 5,3 A 2,2 0,0,1 9,3 Q 9,6 5,9 Q 1,6 1,3 z"
                  scale={2}
                  title="ArrowUp"
                />
              </Text>
            </Wrapper>
          </TwoColumnGrid.MobileOnlySecondary>
        ) : null}
      </TwoColumnGrid>
    </Wrapper>
  );
};

export const checkCopy = (context): CreateAccountCopy => {
  if (context.utility.funnelTrack === FunnelTrack.Waitlist) {
    return waitListCopy(context);
  } else if (context.funnelId === MINNESOTA_LMI_FUNNEL_ID) {
    return {
      ...defaultCopy(context),
      ...minnesotaLmiCopy(),
    };
  } else {
    return defaultCopy(context);
  }
};

export const defaultCopy = ({
  utility,
  zipCode,
  forceWaitlist,
  funnelId,
}: CreateAccountContext) => {
  const savingsRate = getSavingsRateDescription(utility.state.abbreviation, funnelId, utility.id);
  const isNM = utility.state.abbreviation === 'NM';
  const isWaitlist = forceWaitlist || utility.funnelTrack === FunnelTrack.Waitlist;
  const isEnrolledNM = isNM && !isWaitlist;

  return {
    title: `Good news! Community solar is available in your zip code ${zipCode}.`,
    description: `To unlock guaranteed savings of ${
      isEnrolledNM ? 'up to' : ''
    } ${savingsRate} on the solar credits applied to your power bill, create your Arcadia account so that we can match you with a community solar farm in your area.`,
    descriptionTwo: '',
    next: 'Continue',
    imageType: CREATE_ACCOUNT_IMAGE as ImageType,
    disclaimer: `Arcadia is a service provider independent of, and not affiliated with, ${utility.displayName}. ${utility.displayName} has not endorsed this service. Savings rates vary.`,
    learnMoreText: 'Why join a community solar farm?',
    learnMoreModal: COMMUNITY_SOLAR,
    oregonDisclaimerImageType: OREGON_DISCLAIMER,
    oregonDisclaimerText:
      'This organization is operating under the Oregon Community Solar Program. The Oregon Community Solar Program is a state-enabled initiative overseen by the Oregon Public Utility Commission and implemented by Energy Solutions, The Energy Trust of Oregon, and Community Energy Project, and Portland General Electric. The Program provides resources and certification for its Project Managers (PMs) but does not promote any individual PM or PM affiliated organization. For more information about the program, call 1-800-481-0510 or email ',
    virginiaInformation:
      "We're thrilled to connect you to a Community Solar project! Currently, we're prioritizing sign-ups for low to moderate income households. If you don't fit this criteria, please be aware of a potentially longer wait time. Thank you for your understanding and enthusiasm.",
    step: 'Step 1 of 4',
    lmiSavingsPercentageDescription: `*Low-to-moderate income households save ${savingsRate}; all others save 5%.`,
  };
};

export const waitListCopy = ({ utility }: CreateAccountContext) => {
  return {
    title: "Community solar is not yet available in your area, but we're working on it!",
    description:
      "We’re always adding new solar farms. Create your account today and be the first to know when we're available in your area! Once a solar farm is live you'll be able to save up to 10% on your energy costs while supporting clean energy.",
    descriptionTwo:
      "There's no commitment required and no hidden charges. Just give us some basic information about you and your home, and we'll keep you updated on our progress.",
    next: 'Join waitlist',
    imageType: GLOBE_HOME as ImageType,
    disclaimer: `Arcadia is a service provider independent of, and not affiliated with, ${utility.displayName}. ${utility.displayName} has not endorsed this service. Savings rates vary.`,
    learnMoreText: 'Why join a community solar farm?',
    learnMoreModal: COMMUNITY_SOLAR,
    oregonDisclaimerImageType: OREGON_DISCLAIMER,
    oregonDisclaimerText:
      'This organization is operating under the Oregon Community Solar Program. The Oregon Community Solar Program is a state-enabled initiative overseen by the Oregon Public Utility Commission and implemented by Energy Solutions, The Energy Trust of Oregon, and Community Energy Project, and Portland General Electric. The Program provides resources and certification for its Project Managers (PMs) but does not promote any individual PM or PM affiliated organization. For more information about the program, call 1-800-481-0510 or email ',
    virginiaInformation:
      "We're thrilled to connect you to a Community Solar project! Currently, we're prioritizing sign-ups for low to moderate income households. If you don't fit this criteria, please be aware of a potentially longer wait time. Thank you for your understanding and enthusiasm.",
    step: 'Step 1 of 4',
    lmiSavingsPercentageDescription: '',
  };
};

export const minnesotaLmiCopy = () => {
  return {
    description:
      "Create your Arcadia account and we'll match you with a community solar farm in your area. You'll save 10% on the solar credits applied to your power bill, guaranteed. (Savings quoted is for qualifying low-income households. For all others, the savings rate is 5%).",
    learnMoreCopy: {
      sectionTwo: {
        title: 'The savings seem too good to be true! How does it work?',
        text: [
          'States like yours create community solar programs to encourage the development of more clean energy. As part of those programs, residents are guaranteed to save 10% on the solar credits applied to their power bill. (Savings quoted is for qualifying low-income households. For all others, the savings rate is 5%).',
          'As a subscriber, part of your regular power bill gets repurposed to keep the community solar farm running (we handle this part for you). When the farm generates electricity, you get credits back on your bill. Think of it as a perk for supporting clean energy.',
        ],
      },
    },
  };
};

export type CreateAccountCopy = ReturnType<typeof defaultCopy>;

export interface CreateAccountProps {
  context: CreateAccountContext;
  error?: string;
  goTo?: {
    utility: () => void;
  };
  flags?: {
    disableEmail?: boolean;
    hideGoogleButton?: boolean;
    showStripeIdInputs?: boolean;
    showFaqButton?: boolean;
    hidePassword?: boolean;
    hideTerms?: boolean;
    requireBusinessName?: boolean;
    requirePhone?: boolean;
    showPhone?: boolean;
    isInviteCodeRequired?: boolean;
    showZipCode?: boolean;
    hideLearnMoreDialogSelector?: boolean;
    hideBackLink?: boolean;
    hideDisclaimer?: boolean;
    showInviteCode?: boolean;
    showStep?: boolean;
    showBillingCheckbox?: boolean;
  };
  next: (payload: CreateAccountForm) => void;
  overrideCopy?: (context: CreateAccountContext) => Partial<CreateAccountCopy>;
  pending: boolean;
}

export interface CreateAccountForm {
  email: string;
  firstName: string;
  generalContractIds?: number[];
  lastName: string;
  password?: string;
  businessName?: string;
  phone?: string;
}

export interface CreateAccountContext {
  agentName?: string;
  businessName?: string;
  email?: string;
  firstName?: string;
  googleUserError?: string;
  inviteCode?: string;
  lastName?: string;
  password?: string;
  phone?: string;
  optInToSMS?: boolean;
  promoCode?: string;
  utility: Utility;
  zipCode: string;
  funnelId?: string;
  forceWaitlist?: boolean;
  budgetBilling?: boolean;
}
